import React, { useMemo, useState } from "react";
import PermissionActionRow from "./PermissionActionRow/PermissionActionRow";
import { PermissionCategory } from "../../interfaces";
import { useTranslation } from "react-i18next";
import { RoleDetailsPageService } from "../../service/RoleDetailsPageService";
import { useRolePermissionsContext } from "../../context/RolePermissionsContext";
import { TooltipCustom } from "@services/ui-components";

type PermissionModuleSectionProps = {
    className: string;
    category: PermissionCategory;
    moduleCode: string;
};

const PermissionCategoryCard: React.FC<PermissionModuleSectionProps> = ({ className, category, moduleCode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t: translate } = useTranslation();
    const { onChangePermissionCategory, permissions, mode } = useRolePermissionsContext();
    const isViewMode = mode === "view";

    const categoryActions = category.actions;
    const isHasActions = categoryActions && categoryActions.length > 0;
    const toggleOpen = () => setIsOpen((prev) => !prev);

    const isCategoryChecked = useMemo(() => {
        if (!categoryActions.length) {
            return false;
        }

        return categoryActions.every((action) => {
            const isChildrenChecked = action.children.every((child) => child.isActive);

            return action.isActive && isChildrenChecked;
        });
    }, [categoryActions]);

    const isCategoryDependenciesSatisfied = useMemo(() => {
        if (!permissions) {
            return true;
        }

        return RoleDetailsPageService.isCategoryDependenciesSatisfied(permissions.modules, category);
    }, [permissions, category]);

    const shouldShowCategoryTooltip = useMemo(() => {
        if (!category.descriptionInfo) {
            return false;
        }

        const hasDependencies = category.actions.some((action) => action.dependsOn && action.dependsOn.length > 0);

        if (hasDependencies) {
            return !isCategoryDependenciesSatisfied;
        }

        return true;
    }, [category, isCategoryDependenciesSatisfied]);

    return (
        <div className={`border rounded-3 bg-white ${className}`}>
            <div
                className={`d-flex align-items-center justify-content-between px-3 py-3 cursor-pointer ${
                    isOpen ? "border-bottom" : ""
                }`}
                onClick={toggleOpen}
            >
                <h6 className="mb-0 fw-bold fs-normal me-2">
                    {RoleDetailsPageService.renderLabelWithArrow(category.label)}

                    {shouldShowCategoryTooltip && (
                        <TooltipCustom placement="right" text={category.descriptionInfo}>
                            <i className="bi bi-info-circle fs-normal ms-2 i-to-block"></i>
                        </TooltipCustom>
                    )}
                </h6>

                <div className="d-flex align-items-center gap-3">
                    {!isViewMode && (
                        <label
                            className="d-flex align-items-center gap-2 mb-0 fs-7 text-primary-grey text-nowrap"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={isCategoryChecked}
                                disabled={!isCategoryDependenciesSatisfied}
                                onChange={(e) =>
                                    onChangePermissionCategory(moduleCode, category.code, e.target.checked)
                                }
                            />
                            <span className="form-check-label">{translate("Allow all")}</span>
                        </label>
                    )}

                    <button
                        type="button"
                        className="btn btn-sm p-0 border-0 bg-transparent shadow-none"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleOpen();
                        }}
                    >
                        <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} fs-6`} />
                    </button>
                </div>
            </div>

            {isOpen && isHasActions && (
                <div className="m-3">
                    {categoryActions.map((action) => (
                        <React.Fragment key={`${action.code}-${action.order}`}>
                            <PermissionActionRow action={action} moduleCode={moduleCode} />
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

export default React.memo(PermissionCategoryCard);
